import type { ApiFailure, MaintenanceNotice } from './types'

/**
 * THE LATE SIGNAL.
 *
 * /api/v1/me reports what is closed when the app starts. This carries the other
 * case: a window opened while somebody was already working.
 *
 * Without it the two halves of the feature disagree. A user who signs in during
 * platform maintenance is stopped at the door; a user who was already inside
 * when the window opened keeps the whole shell and collects a refusal on each
 * screen they touch — the door-by-door experience this change exists to remove,
 * reappearing for exactly the people an operator is most likely to be
 * interrupting.
 *
 * It is a NOTIFICATION, not a decision. Every 503 it forwards was already
 * produced by the server refusing; nothing here grants access, and suppressing
 * it in a debugger changes what the screen says and nothing about what the API
 * will answer.
 */

type Listener = (notice: MaintenanceNotice) => void

const listeners = new Set<Listener>()

/**
 * Subscribe to windows discovered mid-session. Returns the unsubscribe.
 *
 * A Set rather than a single slot: React's strict mode mounts effects twice, and
 * a single slot would leave the first subscription installed and firing into an
 * unmounted tree.
 */
export function onMaintenanceDetected(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * Called by both API clients when a request is refused with a maintenance 503.
 *
 * Failures are swallowed per listener. This runs inside the failure path of an
 * API call, and a throw here would replace a maintenance error — which the UI
 * knows how to render — with an unexpected one, which it renders as "something
 * went wrong".
 */
export function maintenanceDetected(failure: ApiFailure): void {
  if (failure.kind !== 'maintenance') return

  const scope = failure.scope || 'platform'
  const notice: MaintenanceNotice = {
    scope,
    slug: scope.startsWith('report:') ? scope.slice('report:'.length) : null,
    message: failure.message,
    expectedEndUtc: failure.expectedEndUtc,
    // A 503 means the request was REFUSED, so read grace cannot have applied to
    // it — under read grace the middleware serves the request and attaches a
    // Warning header instead. Anything that reaches here is genuinely closed.
    allowReadGrace: false,
  }

  for (const fn of listeners) {
    try {
      fn(notice)
    } catch {
      /* a listener's problem is not this request's problem */
    }
  }
}
